// packages/console/src/components/TopBar.tsx
//
// The shared SaaS top bar for both consoles (file and live). Chrome is identical
// across the two surfaces: the wordmark + breadcrumb on the left, a per-console
// slot (readiness tag, and for live the timecode + start/stop action), then the
// file/live mode toggle, then the UserMenu. Only the crumb trail and the slot
// differ between the two; the toggle and UserMenu are shared here so the two
// consoles cannot drift apart.

import type { ReactNode } from "react";
import type { SessionState } from "../hooks/useSession";
import { ConsoleCrumb, type CrumbItem } from "./Parts";
import { UserMenu } from "./UserMenu";
import { ModeToggle, type ConsoleMode } from "./ModeToggle";

export function TopBar({
  trail,
  mode,
  onMode,
  session,
  children,
}: {
  trail: CrumbItem[];
  mode: ConsoleMode;
  onMode: (m: ConsoleMode) => void;
  session: SessionState;
  // children is the per-console slot rendered between the crumb and the mode
  // toggle: the file console puts its readiness tag here, the live console its
  // readiness tag, timecode, and start/stop action.
  children?: ReactNode;
}) {
  return (
    <div
      style={{
        height: 56,
        flex: "none",
        display: "flex",
        alignItems: "center",
        gap: 16,
        padding: "0 18px",
        background: "var(--bg-panel)",
        borderBottom: "1px solid var(--line)",
      }}
    >
      <ConsoleCrumb trail={trail} />
      <div style={{ flex: 1 }} />
      {children}
      {/* Mode toggle sits between the per-console slot (readiness tag) and the
          UserMenu, per the design handoff. */}
      <ModeToggle mode={mode} onMode={onMode} />
      <div style={{ width: 1, height: 22, background: "var(--line)" }} />
      <UserMenu session={session} />
    </div>
  );
}
